import {
  Button,
  HStack,
  Field,
  RadioGroup,
  Stack,
  Input,
  ButtonGroup,
  SimpleGrid,
  NativeSelect,
  Tag,
  Text,
  Card,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { useState } from "react";
import { RadioOptions } from "../../../../hla/views/HlaWayDetail/form/RadioOptions";
import {
  booleanOptions,
  presenceDistanceOptions,
  SelectOption,
} from "../../../../hla/views/HlaWayDetail/form/utils";
import { CloseIcon } from "../../../../shared/views/icons";
import { hlaAttributeOptions } from "../../../../map/models/HlaAttribute";

const AddHlaForecastActionModalBase = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { surveyStore, hlaForecastStore } = useStores();

  const [form, setForm] = useState<Record<string, string | null>>({});

  const handleChange = (attributeName: string, value: string | null) => {
    setForm((prev) => ({
      ...prev,
      [attributeName]: value,
    }));
  };

  const handleSubmit = () => {
    hlaForecastStore.addAction(form);
    onClose();
  };

  const clearField = (attributeName: string) =>
    setForm((prev) => {
      delete prev[attributeName];
      return { ...prev };
    });

  const formEntries = Object.entries(form);

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={"Apply forecast action to filtered segments"}
      footer={
        <ButtonGroup colorPalette="primary">
          <Button variant="ghost" onClick={onClose}>
            {t(`shared:close`)}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={Object.keys(form).length === 0}
          >
            Confirm
          </Button>
        </ButtonGroup>
      }
    >
      <Stack gap={4}>
        {formEntries.length > 0 && (
          <HStack wrap={"wrap"}>
            {formEntries.map(([attributeName, value]) => {
              const [name, side] = attributeName.split(".");
              return (
                <Tag.Root key={attributeName} colorPalette={"primary"}>
                  <Tag.Label>
                    {t(`shared:hlaAttribute.${name}`, {
                      speedUnit: surveyStore.speedUnitStr,
                    })}
                    {side && ` (${side})`}: {value ?? "null"}
                  </Tag.Label>

                  <Tag.CloseTrigger onClick={() => clearField(attributeName)}>
                    <CloseIcon />
                  </Tag.CloseTrigger>
                </Tag.Root>
              );
            })}
          </HStack>
        )}

        <Card.Root
          variant={"subtle"}
          px={3}
          py={2}
          textAlign={"center"}
          fontWeight={"normal"}
        >
          Road fields
        </Card.Root>

        <SimpleGrid columns={{ lg: 1, xl: 2 }} gap={4}>
          <Item label={t(`shared:hlaAttribute.type`)}>
            <NativeSelect.Root size="sm" fontSize={"sm"}>
              <NativeSelect.Field
                value={form.type ?? ""}
                placeholder="Select an option"
                onChange={(e) => {
                  const newValue = e.target.value;

                  handleChange("type", newValue === "" ? null : newValue);
                }}
              >
                {hlaAttributeOptions.type!.map((option) => (
                  <option value={option.value} key={option.value}>
                    {t(option.translation)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Item>

          <RadioItem
            attributeName="heavyVehicleFlow"
            options={hlaAttributeOptions.heavyVehicleFlow!}
            {...{ form, handleChange }}
          />

          <Item
            label={t(`shared:hlaAttribute.averageRoadSpeed`, {
              speedUnit: surveyStore.speedUnitStr,
            })}
          >
            <Input
              size={"sm"}
              fontSize={"sm"}
              type="number"
              step={10}
              onChange={(e) => {
                const { value: newValue } = e.target;
                handleChange(
                  "averageRoadSpeed",
                  newValue === "" ? null : newValue,
                );
              }}
              min={0}
              value={form.averageRoadSpeed ?? ""}
            />
          </Item>

          <Item label={t(`shared:hlaAttribute.averageDailyTraffic`)}>
            <Input
              size={"sm"}
              fontSize={"sm"}
              type="number"
              step={1000}
              onChange={(e) => {
                const { value: newValue } = e.target;
                handleChange(
                  "averageDailyTraffic",
                  newValue === "" ? null : newValue,
                );
              }}
              min={0}
              value={form.averageDailyTraffic ?? ""}
            />
          </Item>

          <RadioItem
            attributeName="lit"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="facilityType"
            options={hlaAttributeOptions.facilityType!}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="flowDirection"
            options={hlaAttributeOptions.flowDirection!}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="facilityWidthPerDirection"
            options={hlaAttributeOptions.facilityWidthPerDirection!}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="tramOrTrainRails"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />

          <RadioItem
            attributeName="lightSegregation"
            options={booleanStrOptions}
            {...{ form, handleChange }}
          />
        </SimpleGrid>

        <Card.Root
          variant={"subtle"}
          mt={4}
          px={3}
          py={2}
          textAlign={"center"}
          fontWeight={"normal"}
        >
          Lane fields
        </Card.Root>

        <AdjacentItem
          attributeName="hardSegregation"
          options={booleanStrOptions}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="roadLane"
          options={presenceDistanceOptionsStr}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="numberOfLanes"
          options={hlaAttributeOptions.numberOfLanes!}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="vehicleParking"
          options={presenceDistanceOptionsStr}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="sidewalk"
          options={presenceDistanceOptionsStr}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="objectOrLevelChange"
          options={presenceDistanceOptionsStr}
          {...{ form, handleChange }}
        />

        <AdjacentItem
          attributeName="severeHazard"
          options={presenceDistanceOptionsStr}
          {...{ form, handleChange }}
        />
      </Stack>
    </CustomDialog>
  );
};

const booleanStrOptions = booleanOptions.map((option) => ({
  ...option,
  value: option.value.toString(),
}));

const presenceDistanceOptionsStr = presenceDistanceOptions.map((option) => ({
  ...option,
  value: option.value ? option.value : "null",
}));

export const AddHlaForecastActionModal = observer(
  AddHlaForecastActionModalBase,
);

const Item = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <HStack>
      <Field.Root>
        <Field.Label>{label}</Field.Label>
        {children}
      </Field.Root>
    </HStack>
  );
};

const RadioItem = ({
  attributeName,
  form,
  options,
  handleChange,
}: {
  options: SelectOption[];
  attributeName: string;
  form: Record<string, string | null>;
  handleChange: (attributeName: string, value: string | null) => void;
}) => {
  var strValue = form[attributeName];
  if (form[attributeName] === null) {
    strValue = "null";
  }
  if (form[attributeName] === undefined) {
    strValue = "";
  }

  return (
    <Item label={t(`shared:hlaAttribute.${attributeName}`)}>
      <RadioGroup.Root
        colorPalette={"primary"}
        value={strValue}
        onValueChange={({ value }) => {
          handleChange(attributeName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} />
      </RadioGroup.Root>
    </Item>
  );
};

const AdjacentItem = ({
  attributeName,
  form,
  options,
  handleChange,
}: {
  options: SelectOption[];
  attributeName: string;
  form: Record<string, string | null>;
  handleChange: (attributeName: string, value: string | null) => void;
}) => {
  const leftName = `${attributeName}.left`;
  const rightName = `${attributeName}.right`;

  var valueLeft = valueToString(form[leftName]);
  var valueRight = valueToString(form[rightName]);

  return (
    <HStack justifyContent={"center"}>
      <RadioGroup.Root
        colorPalette={"primary"}
        value={valueLeft}
        onValueChange={({ value }) => {
          handleChange(leftName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} reverse />
      </RadioGroup.Root>

      <Text textStyle={"label"} w="140px" textAlign={"center"}>
        {t(`shared:hlaAttribute.${attributeName}`)}
      </Text>

      <RadioGroup.Root
        colorPalette={"primary"}
        value={valueRight}
        onValueChange={({ value }) => {
          handleChange(rightName, value === "null" ? null : value);
        }}
      >
        <RadioOptions options={options} />
      </RadioGroup.Root>
    </HStack>
  );
};

const valueToString = (formValue: string | undefined | null) => {
  if (formValue === null) {
    return "null";
  }
  if (formValue === undefined) {
    return "";
  }

  return formValue;
};
